/**
 * Domain Enforcement Tab
 *
 * Shows domain-specific enforcement readiness:
 * - Current vs target policy
 * - Risk simulation (what would happen at stricter policies)
 * - Authentication pass rates
 * - Sources breakdown
 * - Blockers and recommended actions
 * - "Advance Policy" button when ready
 */

import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Sparkles,
  Server,
  Zap,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  HelpCircle,
  RefreshCw,
  ArrowRight,
  Copy,
  Check,
  Loader2,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import {
  api,
  DomainReadiness,
  ActionItemsSummary,
  type PolicyLadderStep,
  type ReadinessCheckResult,
} from "@/lib/api";
import { toast } from "@/lib/toast";

interface EnforcementTabProps {
  domainId: string;
  domainName: string;
  tier?: string;
}

// Policy badge component
function PolicyBadge({ policy, size = "md" }: { policy: string | null; size?: "sm" | "md" }) {
  const colors = {
    reject: "bg-emerald-100 text-emerald-700 border-emerald-200",
    quarantine: "bg-amber-100 text-amber-700 border-amber-200",
    none: "bg-slate-100 text-slate-600 border-slate-200",
  };
  const color = colors[(policy as keyof typeof colors) || "none"];
  const sizeClasses = size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm";

  return (
    <span className={`font-medium rounded-full border ${color} ${sizeClasses}`}>
      p={policy || "none"}
    </span>
  );
}

// Severity indicator
function SeverityBadge({ severity }: { severity: string }) {
  const colors = {
    critical: "bg-red-100 text-red-700",
    high: "bg-orange-100 text-orange-700",
    medium: "bg-amber-100 text-amber-700",
    low: "bg-slate-100 text-slate-600",
  };
  return (
    <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${colors[severity as keyof typeof colors] || colors.low}`}>
      {severity}
    </span>
  );
}

/**
 * B0 cutover. Replaces the legacy `ReadinessRing` composite score
 * with a named 11-check rubric. The score told you "78 ready" but
 * no caller could reason about why or what to fix; the rubric
 * names each gate, its current measurement, the threshold, and
 * the reason it's failing.
 *
 * Layout: a "X of N passing" headline (N counts only applicable
 * checks — not_applicable rows are shown but excluded from the
 * denominator so a brand-new domain doesn't read as "0 of 11"
 * just because it has no traffic yet), followed by one row per
 * check. Failing and not_applicable rows always show their reason
 * inline — discoverability over compactness.
 */
/**
 * Tier-aware rubric. Failing rows split visually by tier — blocking
 * (rose), advisory (amber), informational (slate) — so the customer
 * reads "3 problems + 2 clocks running" instead of "5 problems."
 * The headline shows that split explicitly and matches the
 * blocking-count copy on the advance button.
 */
interface RubricCounts {
  passing: number;
  applicable: number;
  blockingFailing: number;
  advisoryFailing: number;
}

function summariseChecks(checks: ReadinessCheckResult[]): RubricCounts {
  const applicable = checks.filter((c) => c.status !== "not_applicable");
  const passing = applicable.filter((c) => c.status === "pass").length;
  const blockingFailing = checks.filter(
    (c) => c.status === "fail" && c.tier === "blocking"
  ).length;
  const advisoryFailing = checks.filter(
    (c) => c.status === "fail" && c.tier === "advisory"
  ).length;
  return {
    passing,
    applicable: applicable.length,
    blockingFailing,
    advisoryFailing,
  };
}

function ReadinessRubric({ checks }: { checks: ReadinessCheckResult[] }) {
  const counts = summariseChecks(checks);
  const notApplicable = checks.length - counts.applicable;
  const pct =
    counts.applicable === 0 ? 0 : (counts.passing / counts.applicable) * 100;
  const barColor =
    counts.blockingFailing === 0 && counts.advisoryFailing === 0
      ? "bg-emerald-500"
      : counts.blockingFailing > 0
        ? "bg-rose-400"
        : "bg-amber-400";

  return (
    <div className="w-full">
      <div className="mb-4">
        <div className="flex items-baseline justify-center gap-1.5">
          <span className="text-4xl font-bold text-slate-900 leading-none tabular-nums">
            {counts.passing}
          </span>
          <span className="text-xl font-medium text-slate-400 leading-none">
            / {counts.applicable}
          </span>
        </div>
        <div className="text-[11px] text-slate-500 text-center uppercase tracking-wider mt-1.5">
          Checks passing
        </div>
        <div
          className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={counts.passing}
          aria-valuemin={0}
          aria-valuemax={counts.applicable}
        >
          <div
            className={`h-full transition-all duration-500 ${barColor}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {(counts.blockingFailing > 0 || counts.advisoryFailing > 0) && (
          <div className="text-[10px] text-center mt-2 flex items-center justify-center gap-2">
            {counts.blockingFailing > 0 && (
              <span className="text-rose-700 font-medium">
                {counts.blockingFailing} blocker
                {counts.blockingFailing === 1 ? "" : "s"}
              </span>
            )}
            {counts.blockingFailing > 0 && counts.advisoryFailing > 0 && (
              <span className="text-slate-300">·</span>
            )}
            {counts.advisoryFailing > 0 && (
              <span className="text-amber-700 font-medium">
                {counts.advisoryFailing} advisory
              </span>
            )}
          </div>
        )}
        {notApplicable > 0 && (
          <div className="text-[10px] text-slate-400 text-center mt-1">
            {notApplicable} not applicable
          </div>
        )}
      </div>
      <ul className="space-y-1">
        {checks.map((check) => (
          <RubricRow key={check.id} check={check} />
        ))}
      </ul>
    </div>
  );
}

function RubricRow({ check }: { check: ReadinessCheckResult }) {
  // Only blocking-tier failures collapse — advisory failures show
  // their (usually short) reason inline because the user benefit
  // is "see the clock or headroom number at a glance," not
  // "investigate this." Blocking reasons are longer and more
  // actionable so collapsing them keeps the list scannable.
  const collapsible = check.status === "fail" && check.tier === "blocking";
  const [expanded, setExpanded] = useState(false);
  const showReason =
    check.reason !== null &&
    (check.status === "not_applicable" ||
      (check.status === "fail" && !collapsible) ||
      (collapsible && expanded));

  // Tier-driven icon: blocking failures → red X; advisory failures →
  // amber triangle ("notice, don't panic"); informational failures →
  // muted ring; pass → green check; n/a → dash.
  const icon =
    check.status === "pass" ? (
      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
    ) : check.status === "fail" ? (
      check.tier === "blocking" ? (
        <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
      ) : check.tier === "advisory" ? (
        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
      ) : (
        <span
          className="w-4 h-4 flex-shrink-0 mt-0.5 inline-flex items-center justify-center text-slate-400 text-xs"
          aria-label="informational"
        >
          ○
        </span>
      )
    ) : (
      <span
        className="w-4 h-4 flex-shrink-0 mt-0.5 inline-flex items-center justify-center text-slate-300 text-xs"
        aria-label="not applicable"
      >
        –
      </span>
    );

  // Tier-coloured row tints. Blocking failures get rose so the eye
  // lands there first; advisory failures get amber (acknowledged
  // but distinct); informational failures get muted slate; not_
  // applicable gets the same muted slate; passing stays clean.
  const rowBg =
    check.status === "fail" && check.tier === "blocking"
      ? "bg-rose-50/40 hover:bg-rose-50"
      : check.status === "fail" && check.tier === "advisory"
        ? "bg-amber-50/40 hover:bg-amber-50"
        : check.status === "fail" && check.tier === "informational"
          ? "bg-slate-50/60"
          : check.status === "not_applicable"
            ? "bg-slate-50/40"
            : "hover:bg-slate-50";

  const nameColor =
    check.status === "not_applicable" ? "text-slate-400" : "text-slate-700";
  const valueColor =
    check.status === "fail" && check.tier === "blocking"
      ? "text-rose-700"
      : check.status === "fail" && check.tier === "advisory"
        ? "text-amber-700"
        : check.status === "not_applicable"
          ? "text-slate-400"
          : "text-slate-500";

  const summary = (
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className={`text-xs font-medium ${nameColor}`}>{check.name}</span>
      <span className="text-[10px] text-slate-300">·</span>
      <span className={`text-[11px] ${valueColor}`}>{check.currentValue}</span>
    </div>
  );

  if (collapsible) {
    return (
      <li className={`rounded ${rowBg} transition-colors`}>
        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          aria-expanded={expanded}
          className="w-full flex items-start gap-2 px-2 py-1.5 text-left"
        >
          {icon}
          <div className="flex-1 min-w-0">{summary}</div>
          <span className="text-slate-400 flex-shrink-0 mt-0.5">
            {expanded ? (
              <ChevronDown className="w-3.5 h-3.5" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5" />
            )}
          </span>
        </button>
        {showReason && (
          <div className="px-2 pb-2 -mt-0.5 pl-8">
            <p className="text-[11px] text-slate-600 leading-relaxed">
              {check.reason}
            </p>
            {check.evidenceLink && (
              <Link
                to={check.evidenceLink}
                className="inline-block mt-1.5 text-[11px] font-medium text-rose-700 hover:text-rose-900"
              >
                View evidence →
              </Link>
            )}
          </div>
        )}
      </li>
    );
  }

  const inner = (
    <div className={`flex items-start gap-2 px-2 py-1.5 ${rowBg} rounded`}>
      {icon}
      <div className="flex-1 min-w-0">
        {summary}
        {showReason && (
          <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">
            {check.reason}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <li>
      {check.evidenceLink && check.status === "pass" ? (
        <Link to={check.evidenceLink} className="block">
          {inner}
        </Link>
      ) : (
        inner
      )}
    </li>
  );
}

// Insight type icon (matches dashboard style)
function InsightIcon({ type, severity }: { type: string; severity: string }) {
  const iconClass = "w-4 h-4 flex-shrink-0";
  const colorClass = severity === "critical" ? "text-red-600" :
                     severity === "high" ? "text-orange-500" :
                     severity === "medium" ? "text-amber-500" :
                     "text-blue-500";

  switch (type) {
    case "provider_issue":
      return <Server className={`${iconClass} ${colorClass}`} />;
    case "spoofing_detected":
      return <XCircle className={`${iconClass} ${colorClass}`} />;
    case "authentication_issue":
      return <AlertTriangle className={`${iconClass} ${colorClass}`} />;
    case "forwarder_impact":
      return <Shield className={`${iconClass} text-blue-500`} />;
    default:
      return <Sparkles className={`${iconClass} ${colorClass}`} />;
  }
}

/**
 * Horizontal stepper showing the staged-rollout ladder from the
 * current pinned position forward to p=reject pct=100. Each step is
 * a small pill labelled "q10" / "q25" / ... / "r100"; the current
 * position is pinned with a filled marker; the immediate next step
 * is highlighted. Hidden when fully enforced.
 *
 * Why: pre-fix the tile only showed "current -> target" with no
 * indication of the runway between them. A caller advancing from
 * p=none to p=reject is actually committing to a 7-step journey
 * (q10 -> q25 -> q50 -> q100 -> r25 -> r50 -> r100); rendering the
 * full path makes that explicit and removes the "I thought one
 * click would land me at full reject" surprise.
 */
function PolicyRamp({
  currentPolicy,
  currentPolicyPct,
  ladder,
}: {
  currentPolicy: "none" | "quarantine" | "reject" | null;
  currentPolicyPct: number;
  ladder: PolicyLadderStep[];
}) {
  // Always include the current position as the first marker so the
  // ramp reads "you are here -> next -> ... -> done". The server's
  // ladder is forward-only, so we prepend a synthetic "current" step.
  const currentLabel =
    currentPolicy === null
      ? "none"
      : pctShortLabel(currentPolicy, currentPolicyPct);
  const nextStep = ladder[0];
  return (
    <div className="mt-3">
      <div className="flex items-center gap-1 flex-wrap justify-center">
        <RampPill label={currentLabel} state="current" />
        {ladder.map((step, idx) => (
          <span key={`${step.p}-${step.pct}`} className="flex items-center gap-1">
            <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
            <RampPill
              label={pctShortLabel(step.p, step.pct)}
              state={idx === 0 ? "next" : "future"}
            />
          </span>
        ))}
      </div>
      {nextStep && (
        <p className="text-xs text-slate-500 text-center mt-2">
          Next: {nextStep.label}
        </p>
      )}
    </div>
  );
}

function RampPill({
  label,
  state,
}: {
  label: string;
  state: "current" | "next" | "future";
}) {
  const styles =
    state === "current"
      ? "bg-slate-800 text-white border-slate-800"
      : state === "next"
        ? "bg-emerald-50 text-emerald-800 border-emerald-300"
        : "bg-white text-slate-400 border-slate-200";
  return (
    <span
      className={`inline-block text-[10px] font-mono px-1.5 py-0.5 rounded border ${styles}`}
    >
      {label}
    </span>
  );
}

/**
 * Short pct-aware label for ramp pills: "none", "q10", "q25", "q100",
 * "r25", ... "r100". Uses single-letter policy prefix to keep the
 * pills compact horizontally; the full "p=quarantine pct=10" form is
 * spelled out below the ramp under "Next:" and on the advance button.
 */
function pctShortLabel(p: string, pct: number): string {
  if (p === "none") return "none";
  const prefix = p === "quarantine" ? "q" : p === "reject" ? "r" : p;
  return `${prefix}${pct}`;
}

/**
 * Failure breakdown panel — shows what kind of failures the headline
 * dmarcFailures.count is made of. Renders only the buckets that
 * actually have volume so a clean "100% Approved" or "100% Not-Mine"
 * domain doesn't get noise from empty rows.
 *
 * Why: pre-fix the count was an opaque 10,428 messages with no
 * indication that some are real business mail (the bad one to lose),
 * some are spoofers (the win to block), some are forwarders ARC could
 * save, etc. Naming the buckets turns the number from a single risk
 * figure into a triage rubric.
 */
function FailureBreakdown({
  breakdown,
  totalFailing,
}: {
  breakdown: {
    approved: number;
    unclassified: number;
    notMine: number;
    forwarder: number;
    unmatched: number;
  };
  totalFailing: number;
}) {
  if (totalFailing === 0) return null;

  // Ordered by how the user reasons about advance-impact, not by
  // volume: "real mail" is the cost, "spoofers" is the win,
  // forwarders are the recoverable bit, unclassified/unmatched
  // are the unknowns to triage before advancing.
  const rows: Array<{
    key: keyof typeof breakdown;
    label: string;
    tone: "cost" | "win" | "neutral" | "unknown";
    hint: string;
  }> = [
    {
      key: "approved",
      label: "Approved",
      tone: "cost",
      hint: "real business mail — would land in spam / get bounced",
    },
    {
      key: "notMine",
      label: "Not-mine",
      tone: "win",
      hint: "spoofers you marked unauthorized — advancing blocks these",
    },
    {
      key: "forwarder",
      label: "Forwarder",
      tone: "neutral",
      hint: "forwarded mail — ARC signing on the sender side would rescue",
    },
    {
      key: "unclassified",
      label: "Unclassified",
      tone: "unknown",
      hint: "sources you have not triaged — could be real or spoofing",
    },
    {
      key: "unmatched",
      label: "Unmatched",
      tone: "unknown",
      hint: "observations awaiting matcher rerun",
    },
  ];

  const toneColor: Record<typeof rows[number]["tone"], string> = {
    cost: "text-rose-700",
    win: "text-emerald-700",
    neutral: "text-sky-700",
    unknown: "text-slate-600",
  };

  return (
    <div className="pt-2 border-t border-amber-200 space-y-0.5 text-xs">
      {rows.map(({ key, label, tone, hint }) => {
        const value = breakdown[key];
        if (value === 0) return null;
        const pct = (value / totalFailing) * 100;
        return (
          <div
            key={key}
            className="flex items-baseline gap-1"
            title={hint}
          >
            <span className={`font-medium ${toneColor[tone]}`}>{label}:</span>
            <span className="text-amber-900">
              {value.toLocaleString()}{" "}
              <span className="text-amber-600">
                ({pct < 1 ? "<1" : Math.round(pct)}%)
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Format an ISO timestamp as a short local "as of" string. Used by
 * the readiness card so the user can tell same-instant snapshots from
 * stale ones at a glance (PR 30, H1 fix). Returns just "today HH:MM"
 * when the timestamp is from today (the common case); falls back to
 * the locale date string when it's older.
 */
function formatAsOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return sameDay ? `today ${time}` : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

/**
 * Render the "since YYYY-MM-DD · N days" line under the policy badge.
 * Why we surface this: quarterly senders, annual senders, and
 * tax-season campaigns all hide from a 30-day window — the longer the
 * domain has dwelled under the current policy, the more confident an
 * advancement decision can be. Format intentionally muted (xs / slate)
 * so it reads as supporting detail to the badge above, not a metric.
 *
 * Returns the empty string for malformed timestamps so the caller's
 * conditional render of the wrapper still hides the line cleanly.
 */
function formatPolicyDwell(iso: string): string {
  const since = new Date(iso);
  if (Number.isNaN(since.getTime())) return "";
  const dateStr = since.toISOString().split("T")[0];
  const days = Math.max(
    0,
    Math.floor((Date.now() - since.getTime()) / (24 * 60 * 60 * 1000))
  );
  return `since ${dateStr} · ${days} ${days === 1 ? "day" : "days"}`;
}

export function EnforcementTab({ domainId, domainName, tier }: EnforcementTabProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<DomainReadiness | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [policyModal, setPolicyModal] = useState<{
    open: boolean;
    targetPolicy: "quarantine" | "reject";
    record?: string;
    /** B6. Currently-published DMARC verbatim; null when no record was published. */
    rollbackRecord?: string | null;
    hostName?: string;
    loading: boolean;
    verifying: boolean;
    verified?: boolean;
    verifyMessage?: string;
    copied: boolean;
    /** B6. Tracks the rollback-record copy button's "Copied!" state. */
    copiedRollback: boolean;
  } | null>(null);

  // Action items for this domain
  const [actionItems, setActionItems] = useState<ActionItemsSummary["items"]>([]);

  async function fetchData() {
    try {
      const response = await api.getEnforcementReadiness();
      const domainData = response.domains.find((d) => d.id === domainId);
      setData(domainData || null);
    } catch (err) {
      console.error("Failed to fetch enforcement data:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  async function fetchActionItems() {
    try {
      const summary = await api.getActionItemsSummary(domainId);
      setActionItems(
        summary.items
          .filter((i) => !i.isCompleted)
          .sort((a, b) => {
            const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
            return (order[a.priority] ?? 4) - (order[b.priority] ?? 4);
          })
      );
    } catch (err) {
      console.error("Failed to fetch action items:", err);
    }
  }

  useEffect(() => {
    fetchData();
    if (tier !== "free") {
      fetchActionItems();
    }
  }, [domainId, tier]);

  async function handleRefresh() {
    setRefreshing(true);
    await Promise.all([fetchData(), fetchActionItems()]);
  }

  async function handleAdvancePolicy() {
    if (!data) return;

    const nextPolicy = (data.currentPolicy === "none" ? "quarantine" : "reject") as "quarantine" | "reject";
    setPolicyModal({
      open: true,
      targetPolicy: nextPolicy,
      loading: true,
      verifying: false,
      copied: false,
      copiedRollback: false,
    });

    try {
      const result = await api.advanceDomainPolicy(domainId, nextPolicy);
      setPolicyModal((prev) =>
        prev
          ? {
              ...prev,
              loading: false,
              record: result.record,
              rollbackRecord: result.rollbackRecord ?? null,
              hostName: result.hostName,
            }
          : null
      );
    } catch (err) {
      console.error("Failed to generate DMARC record:", err);
      toast.error("Failed to generate DMARC record");
      setPolicyModal(null);
    }
  }

  async function handleVerifyDns() {
    if (!policyModal) return;

    setPolicyModal((prev) => prev ? { ...prev, verifying: true, verified: undefined, verifyMessage: undefined } : null);

    try {
      const result = await api.advanceDomainPolicy(domainId, policyModal.targetPolicy, true);
      setPolicyModal((prev) =>
        prev
          ? {
              ...prev,
              verifying: false,
              verified: result.verified,
              verifyMessage: result.verified
                ? `Policy updated to p=${policyModal.targetPolicy}`
                : result.message,
            }
          : null
      );

      if (result.verified) {
        // Refresh enforcement data after short delay
        setTimeout(() => {
          setPolicyModal(null);
          fetchData();
        }, 2000);
      }
    } catch {
      setPolicyModal((prev) =>
        prev
          ? { ...prev, verifying: false, verified: false, verifyMessage: "Failed to verify DNS. Please try again." }
          : null
      );
    }
  }

  async function handleCopyRollback() {
    if (!policyModal?.rollbackRecord) return;
    try {
      await navigator.clipboard.writeText(policyModal.rollbackRecord);
      setPolicyModal((prev) => prev ? { ...prev, copiedRollback: true } : null);
      setTimeout(
        () => setPolicyModal((prev) => prev ? { ...prev, copiedRollback: false } : null),
        2000
      );
    } catch (err) {
      console.error("Failed to copy rollback record:", err);
      toast.error("Failed to copy");
    }
  }

  async function handleCopyRecord() {
    if (!policyModal?.record) return;
    try {
      await navigator.clipboard.writeText(policyModal.record);
      setPolicyModal((prev) => prev ? { ...prev, copied: true } : null);
      setTimeout(() => setPolicyModal((prev) => prev ? { ...prev, copied: false } : null), 2000);
    } catch {
      // Fallback for clipboard API not available
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <ShieldAlert className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          Enforcement data not available
        </h3>
        <p className="text-slate-500 mb-4">
          We couldn't load enforcement readiness for this domain.
        </p>
        <button
          onClick={handleRefresh}
          className="text-amber-600 hover:text-amber-700 font-medium inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  const isAtFullEnforcement = data.currentPolicy === "reject";
  const hasBlockers = data.blockers.length > 0;
  const criticalBlockers = data.blockers.filter((b) => b.severity === "critical");

  return (
    <div className="space-y-6">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-amber-500" />
          <h3 className="text-lg font-semibold text-slate-800">Enforcement Readiness</h3>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Readiness Statement Banner */}
      {data.readinessStatement && (
        <div
          className={`rounded-xl p-4 ${
            data.canAdvance
              ? "bg-emerald-50 border border-emerald-200"
              : data.authentication.dmarcPassRate >= 95
                ? "bg-blue-50 border border-blue-200"
                : "bg-amber-50 border border-amber-200"
          }`}
        >
          <p
            className={`text-sm font-medium ${
              data.canAdvance
                ? "text-emerald-700"
                : data.authentication.dmarcPassRate >= 95
                  ? "text-blue-700"
                  : "text-amber-800"
            }`}
          >
            {data.readinessStatement}
          </p>
        </div>
      )}

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Readiness Rubric & Policy (B0 cutover —
            replaces the legacy ReadinessRing composite score) */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex flex-col items-center text-center">
            <ReadinessRubric checks={data.checks} />

            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-center gap-2">
                <span className="text-sm text-slate-500">Current:</span>
                <PolicyBadge policy={data.currentPolicy} />
                {data.currentPolicy !== "none" && data.currentPolicyPct < 100 && (
                  <span className="text-xs text-slate-500">pct={data.currentPolicyPct}</span>
                )}
              </div>
              {data.currentPolicySetAt && (
                <div className="text-xs text-slate-500">
                  {formatPolicyDwell(data.currentPolicySetAt)}
                </div>
              )}
              {/* Note: the standalone "Target:" badge was replaced by the
                  PolicyRamp stepper below — the ramp ends at p=reject
                  pct=100, which is the implicit target for every domain
                  not yet fully enforced. Re-introducing a separate
                  Target badge alongside the ramp would just duplicate
                  the same signal in two different visual styles. */}
            </div>

            {/* Staged-rollout ladder. Shows the runway from the current
                pinned position to p=reject pct=100 so callers see "I'm
                at q=50 and there are 4 steps left" instead of just
                "current -> target". Hidden when fully enforced. */}
            {!isAtFullEnforcement && data.policyLadder.length > 0 && (
              <PolicyRamp
                currentPolicy={data.currentPolicy}
                currentPolicyPct={data.currentPolicyPct}
                ladder={data.policyLadder}
              />
            )}

            {/* Advance Policy Button. Label comes from the server-side
                NextStep so it reads "Advance to p=quarantine pct=10"
                with the actual proposed pct, not the generic
                "Advance to p=quarantine" derived from currentPolicy
                alone. */}
            {!isAtFullEnforcement && data.proposedNextStep && (
              data.canAdvance ? (
                <button
                  onClick={handleAdvancePolicy}
                  disabled={!!policyModal}
                  className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  <ShieldCheck className="w-4 h-4" />
                  Advance to {data.proposedNextStep.label}
                </button>
              ) : (
                <div className="mt-4">
                  <button
                    disabled
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-400 font-medium rounded-lg cursor-not-allowed"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Advance to {data.proposedNextStep.label}
                  </button>
                  {(() => {
                    // Reconcile button text with the rubric's tiered
                    // counting. Pre-fix the count came from data.blockers
                    // (a legacy advisory + blocking lump) and the rubric
                    // showed a different count of failing rows; users
                    // got "3 blockers" on the button next to "5 red
                    // checks" on the rubric. Source of truth is the
                    // rubric now — blocking-tier failures only.
                    const blockingFailing = data.checks.filter(
                      (c) => c.status === "fail" && c.tier === "blocking"
                    ).length;
                    if (blockingFailing === 0) return null;
                    return (
                      <p className="text-xs text-slate-500 text-center mt-2">
                        {blockingFailing} blocker
                        {blockingFailing !== 1 ? "s" : ""} must be resolved first
                      </p>
                    );
                  })()}
                </div>
              )
            )}

            {isAtFullEnforcement && (
              <div className="mt-4 flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Full Enforcement</span>
              </div>
            )}
          </div>
        </div>

        {/* Middle Column: Risk Simulation */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-slate-500" />
            Risk Simulation (Last {data.riskSimulation.timeframeDays} Days)
          </h4>

          <div className="space-y-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">Total Messages</div>
              <div className="text-xl font-semibold text-slate-800">
                {data.riskSimulation.totalMessages.toLocaleString()}
              </div>
            </div>

            {/* PR 35 (M4 fix). The Risk Simulation card used to show
              * two side-by-side boxes for atQuarantine and atReject
              * with byte-identical "would fail" counts — same
              * DMARC-fail set acted on by either policy. The redesign
              * review called two-boxes-one-number out as reading
              * like a bug whether or not it is one.
              *
              * Single count headline now, with the per-policy
              * consequence spelled out below it. The figure (how many
              * messages fail DMARC) is one number; the disposition
              * (where those messages end up) genuinely differs:
              * quarantine routes them to spam folders, reject bounces
              * them. Showing one figure with the disposition fork made
              * explicit keeps the data honest and doesn't lose the
              * impact-differs signal the two-box layout was reaching
              * for. */}
            <div className="p-3 bg-amber-50 rounded-lg space-y-2">
              <div>
                <div className="text-xs text-amber-600 mb-1">
                  Messages failing DMARC
                </div>
                <div className="text-lg font-semibold text-amber-700">
                  {data.riskSimulation.dmarcFailures.percentage.toFixed(1)}%
                </div>
                <div className="text-xs text-amber-600">
                  {data.riskSimulation.dmarcFailures.count.toLocaleString()} messages
                </div>
              </div>
              <FailureBreakdown
                breakdown={data.riskSimulation.dmarcFailures.breakdown}
                totalFailing={data.riskSimulation.dmarcFailures.count}
              />
              <div className="pt-2 border-t border-amber-200 space-y-0.5 text-xs">
                <div className="text-amber-800">
                  <span className="font-medium">At p=quarantine:</span>{" "}
                  routed to spam
                </div>
                <div className="text-amber-800">
                  <span className="font-medium">At p=reject:</span>{" "}
                  bounced
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Authentication */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-4">
            Authentication Pass Rates
          </h4>

          <div className="space-y-4">
            {/* SPF */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">SPF</span>
                <div className="flex items-center gap-2">
                  {data.authentication.spfConfigured ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span className={`text-sm font-medium ${
                    data.authentication.spfPassRate >= 95 ? "text-emerald-700" : "text-amber-600"
                  }`}>
                    {data.authentication.spfPassRate}%
                  </span>
                </div>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    data.authentication.spfPassRate >= 95 ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                  style={{ width: `${data.authentication.spfPassRate}%` }}
                />
              </div>
            </div>

            {/* DKIM */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">DKIM</span>
                <div className="flex items-center gap-2">
                  {data.authentication.dkimConfigured ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600" />
                  )}
                  <span className={`text-sm font-medium ${
                    data.authentication.dkimPassRate >= 95 ? "text-emerald-700" : "text-amber-600"
                  }`}>
                    {data.authentication.dkimPassRate}%
                  </span>
                </div>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    data.authentication.dkimPassRate >= 95 ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                  style={{ width: `${data.authentication.dkimPassRate}%` }}
                />
              </div>
            </div>

            {/* DMARC */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-600">DMARC</span>
                <span className={`text-sm font-medium ${
                  data.authentication.dmarcPassRate >= 95 ? "text-emerald-700" : "text-amber-600"
                }`}>
                  {data.authentication.dmarcPassRate}%
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    data.authentication.dmarcPassRate >= 95 ? "bg-emerald-500" : "bg-amber-500"
                  }`}
                  style={{ width: `${data.authentication.dmarcPassRate}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sources Summary */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-slate-700">
            Sending Sources
          </h4>
          <Link
            to={`?tab=sources`}
            className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
          >
            Manage Sources
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>

        {/* PR 30 (M1 fix) — switched from the legacy Authorized/Pending/
            Unauthorized vocabulary to the spine's Ready / Review /
            Needs Work triage so this card speaks the same language
            as the /sources page. byBucket comes from the API; the
            legacy authorized/pendingReview/unauthorized fields stay
            on the response only for back-compat. */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-slate-50 rounded-lg">
            <div className="text-xl font-bold text-slate-800">{data.sources.total}</div>
            <div className="text-xs text-slate-500">Total</div>
          </div>
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <div className="text-xl font-bold text-emerald-700">{data.sources.byBucket.ready}</div>
            <div className="text-xs text-emerald-700">Ready</div>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <div className="text-xl font-bold text-amber-600">{data.sources.byBucket.review}</div>
            <div className="text-xs text-amber-600">Review</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-xl font-bold text-red-600">{data.sources.byBucket.needsWork}</div>
            <div className="text-xs text-red-600">Needs Work</div>
          </div>
        </div>

        {/* Blocking Sources */}
        {data.sources.blockingSources.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <h5 className="text-xs font-medium text-slate-600 uppercase tracking-wider mb-3">
              Sources Blocking Advancement
            </h5>
            <div className="space-y-2">
              {data.sources.blockingSources.slice(0, 3).map((source) => (
                <div
                  key={source.id}
                  className="flex items-center justify-between p-2 bg-amber-50 rounded-lg"
                >
                  <div>
                    {/* PR 30 (B2 fix). Identifier preference flipped on the
                        server: provider name now wins, falling through to
                        the auth identity, and only the synthetic-only case
                        falls back to a raw CIDR. The secondary line shows
                        the auth identity (em539.pacificdebt.com) when the
                        primary is the provider name, so two SendGrid rows
                        with different auth identities are visually
                        distinct on the Overview the same way they are on
                        the /sources list. */}
                    <div className="text-sm font-medium text-slate-800">
                      {source.name || source.providerName || source.identifier}
                    </div>
                    {source.providerName && source.authIdentity && (
                      <div className="text-xs text-slate-500 font-mono">
                        {source.authIdentity}
                      </div>
                    )}
                    {/* B-e. ASN-derived context line for sources whose
                        identifier is a raw CIDR or synthetic ID — turns
                        "35.174.145.0/24" into "Amazon AWS · unidentified
                        operator." Server-populated only when present;
                        the row stays clean for fully-identified sources. */}
                    {source.enrichmentLabel && (
                      <div className="text-xs text-slate-500">
                        {source.enrichmentLabel}
                      </div>
                    )}
                    <span className="text-xs text-amber-600">{source.issue}</span>
                  </div>
                  <Link
                    to={`/sources/${source.id}`}
                    className="text-xs font-medium text-amber-600 hover:text-amber-800"
                  >
                    {source.fix}
                  </Link>
                </div>
              ))}
              {data.sources.blockingSources.length > 3 && (
                <Link
                  to={`/sources?domain=${domainId}&filter=needs_work`}
                  className="block text-xs text-amber-600 hover:text-amber-700 font-medium text-center"
                >
                  +{data.sources.blockingSources.length - 3} more blocking sources
                </Link>
              )}
            </div>
          </div>
        )}

        {/* PR 30 (H1 fix). As-of stamp so this card can't be confused
            with a stale-by-seconds view of the dashboard. The whole
            payload carries one windowEndsAt; rendering it once at the
            card footer covers all the figures inside. */}
        <div className="mt-3 text-[10px] text-slate-400 text-right">
          As of {formatAsOf(data.windowEndsAt)} · trailing 30 days
        </div>
      </div>

      {/* Action Items — domain-specific findings (Dashboard-style compact cards) */}
      {tier === "free" ? (
        <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-slate-800">Action Items</h4>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Get AI-powered analysis of your domain's email authentication, sender behavior, and security posture.
          </p>
          <button
            onClick={() => navigate("/settings/billing")}
            className="text-sm font-medium text-purple-600 hover:text-purple-700"
          >
            Upgrade to unlock Action Items
          </button>
        </div>
      ) : actionItems.length > 0 ? (
        <div className="bg-gradient-to-br from-purple-50 to-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-slate-800">Action Items</h4>
              <span className="px-1.5 py-0.5 bg-purple-600 text-white text-xs font-medium rounded-full">
                {actionItems.length}
              </span>
            </div>
            <button
              onClick={() => navigate("/alerts")}
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              View All
            </button>
          </div>
          <div className="space-y-2">
            {actionItems.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate("/alerts")}
                className="w-full text-left p-3 bg-white rounded-lg border border-purple-100 hover:border-purple-200 hover:shadow-sm transition-all"
              >
                <div className="flex items-start gap-2">
                  <InsightIcon type={item.source} severity={item.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-slate-800 text-sm">
                        {item.title}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        item.priority === "critical" ? "bg-red-100 text-red-700" :
                        item.priority === "high" ? "bg-orange-100 text-orange-700" :
                        item.priority === "medium" ? "bg-amber-100 text-amber-700" :
                        item.priority === "low" ? "bg-blue-100 text-blue-700" :
                        "bg-slate-100 text-slate-600"
                      }`}>
                        {item.priority}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-2">
                      {item.description}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : hasBlockers ? (
        /* Fallback: generic blockers when no AI insights exist */
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${criticalBlockers.length > 0 ? "text-red-600" : "text-amber-500"}`} />
            {data.authentication.dmarcPassRate >= 95 ? "Remaining Items" : "Items to Address"}
            <span className="text-xs font-normal text-slate-400">({data.blockers.length})</span>
          </h4>

          <div className="space-y-3">
            {data.blockers.map((blocker, idx) => {
              const bgColor =
                blocker.severity === "critical" ? "bg-red-50 border-red-100" :
                blocker.severity === "high" ? "bg-orange-50 border-orange-100" :
                blocker.severity === "medium" ? "bg-amber-50 border-amber-100" :
                "bg-slate-50 border-slate-200";
              const iconColor =
                blocker.severity === "critical" ? "text-red-600" :
                blocker.severity === "high" ? "text-orange-600" :
                blocker.severity === "medium" ? "text-amber-600" :
                "text-slate-500";

              return (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-4 rounded-lg border ${bgColor}`}
                >
                  <AlertTriangle className={`w-4 h-4 ${iconColor} mt-0.5 flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-800">
                        {blocker.title}
                      </span>
                      <SeverityBadge severity={blocker.severity} />
                    </div>
                    <p className="text-xs text-slate-600 mt-1">{blocker.description}</p>
                    {blocker.affectedMessages > 0 && (
                      <p className="text-xs text-slate-500 mt-1">
                        {blocker.affectedMessages.toLocaleString()} messages affected
                      </p>
                    )}
                  </div>
                  {blocker.remediation.link && (
                    <Link
                      to={blocker.remediation.link}
                      className={`text-xs font-medium ${iconColor} hover:opacity-75 flex items-center gap-1 flex-shrink-0`}
                    >
                      Fix
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Next Step / Recommended Action */}
      {data.recommendedAction && !isAtFullEnforcement && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-800 mb-1">
                {data.recommendedAction.title}
              </h4>
              <p className="text-sm text-slate-600 mb-3">
                {data.recommendedAction.description}
              </p>
              {data.recommendedAction.link && (
                <Link
                  to={data.recommendedAction.link}
                  className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 hover:text-amber-800"
                >
                  Take Action
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Help Link */}
      <div className="flex justify-center">
        <Link
          to="/help#enforcement"
          className="text-sm text-slate-400 hover:text-amber-600 flex items-center gap-1"
        >
          <HelpCircle className="w-4 h-4" />
          Learn more about enforcement
        </Link>
      </div>

      {/* Policy Advancement Modal */}
      <Modal
        open={!!policyModal?.open}
        onOpenChange={(open) => !open && setPolicyModal(null)}
      >
        <Modal.Content
          title={`Advance ${domainName} to p=${policyModal?.targetPolicy ?? ""}`}
          size="lg"
        >
          {/* Modal Body */}
          <div className="p-4 space-y-4">
            {policyModal?.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-600">
                  Update the DNS TXT record at <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs font-mono">{policyModal?.hostName}</code> with the following value:
                </p>

                {/* Record Display */}
                <div className="relative">
                  <pre className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono text-slate-800 whitespace-pre-wrap break-all">
                    {policyModal?.record}
                  </pre>
                  <button
                    onClick={handleCopyRecord}
                    className="absolute top-2 right-2 p-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                    title="Copy to clipboard"
                  >
                    {policyModal?.copied ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-slate-500" />
                    )}
                  </button>
                </div>

                <p className="text-xs text-slate-500">
                  After updating your DNS record, click "Verify DNS" to confirm the change. DNS propagation may take a few minutes.
                </p>

                {/* B6: Rollback panel. Shows the currently-published
                    record verbatim so the caller can copy a back-out
                    string before applying the advance. A back-out plan
                    is part of readiness, not after-the-fact incident
                    response — surfacing it here removes the "what if
                    this breaks" hesitation that keeps callers stuck
                    at p=none for months. */}
                {policyModal?.rollbackRecord ? (
                  <div className="border-t border-slate-200 pt-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-700">
                        Rollback record (if you need to revert)
                      </span>
                      <span className="text-xs text-slate-400">
                        re-publish to undo
                      </span>
                    </div>
                    <div className="relative">
                      <pre className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-700 whitespace-pre-wrap break-all">
                        {policyModal.rollbackRecord}
                      </pre>
                      <button
                        onClick={handleCopyRollback}
                        className="absolute top-2 right-2 p-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                        title="Copy rollback record"
                      >
                        {policyModal.copiedRollback ? (
                          <Check className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-slate-500" />
                        )}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-t border-slate-200 pt-3 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">To revert:</span>{" "}
                    delete the DMARC TXT record entirely — this domain
                    has no published DMARC string to roll back to.
                  </div>
                )}

                {/* Verification Result */}
                {policyModal?.verified !== undefined && (
                  <div
                    className={`p-3 rounded-lg text-sm ${
                      policyModal.verified
                        ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                        : "bg-amber-50 border border-amber-200 text-amber-700"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {policyModal.verified ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span>{policyModal.verifyMessage}</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Modal Footer */}
          {policyModal && !policyModal.loading && (
            <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-200">
              <button
                onClick={() => setPolicyModal(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800"
              >
                {policyModal.verified ? "Done" : "Cancel"}
              </button>
              {!policyModal.verified && (
                <button
                  onClick={handleVerifyDns}
                  disabled={policyModal.verifying}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {policyModal.verifying ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Verify DNS
                </button>
              )}
            </div>
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
}